import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { memoNamed } from '../utils/react/memo-named';
import { MyPageInfo } from './MyPageInfo';
import {
  userWithDefault,
  userInfoTagList,
  isUserInfo,
  User,
  Check,
  hasPrivilege,
} from '../types/user';
import { H3, Hr, Explanation, Root } from './common/styled';
import { Page } from './App';
import { Setting } from './Setting';
import { MyPageContact } from './MyPageContact';
import { MyPageAddress } from './MyPageAddress';

interface UserInfoItem {
  tagKey: keyof User;
  tagName: string;
  privateIsDisabled: boolean;
  inputValue: string;
  isPublic: boolean;
  check: (input: string) => Check;
}
interface Props {
  gotoPage: (page: Page) => void;
}

const checkDummy = (input: string): Check => {
  console.log(input);
  return {
    isCorrect: true,
    alert: '',
  };
};

export const MyPage = memoNamed<Props>('MyPage', (props) => {
  const [userInfoList, setUserInfoList] = useState<UserInfoItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [privilegePeriod, setPrivilegePeriod] = useState<
    { start: firebase.firestore.Timestamp; end: firebase.firestore.Timestamp }[]
  >([]);
  const [showPrivilegePeriod, setShowPrivilegePeriod] = useState<boolean>(
    false
  );
  const [emailInvalid, setEmailInvalid] = useState<boolean>(false);
  const [currentInvalid, setCurrentInvalid] = useState<boolean>(false);
  const [homeInvalid, setHomeInvalid] = useState<boolean>(false);

  useEffect(() => {
    const user = firebase.auth().currentUser;
    const db = firebase.firestore();

    setIsLoading(true);
    db.collection('Users')
      .where('userID', '==', user?.uid)
      .get()
      .then((snapshot) => {
        const userData = userWithDefault(snapshot.docs[0].data()); // TODO: check
        setIsLoading(false);
        setPrivilegePeriod(userData.privilegePeriod);
        setEmailInvalid(userData.emailValid === 'invalid');
        setCurrentInvalid(userData.currentValid === 'invalid');
        setHomeInvalid(userData.homeValid === 'invalid');
        setUserInfoList(
          userInfoTagList.map(
            ({ tagKey, tagName, check }): UserInfoItem => {
              const userInfoOrOther = userData[tagKey];
              if (isUserInfo(userInfoOrOther)) {
                return {
                  tagKey,
                  tagName,
                  privateIsDisabled: false,
                  inputValue: userInfoOrOther.value,
                  isPublic: userInfoOrOther.open,
                  check,
                };
              } else {
                return {
                  tagKey,
                  tagName,
                  privateIsDisabled: true,
                  inputValue: userInfoOrOther.toString(),
                  isPublic: false,
                  check,
                };
              }
            }
          )
        );
      })
      .catch(() => {
        setMessage('データの取得に失敗しました。');
        setIsLoading(false);
      });
  }, []);

  const basicInfoList = useMemo(
    () =>
      userInfoList.filter((u) =>
        [
          'sei',
          'mei',
          'seiKana',
          'meiKana',
          'sex',
          'kyusei',
          'kyuseiKana',
          'enterYear',
          'enterUniversityYear',
          'finalEducation',
        ].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const email = useMemo(() => userInfoList.find((u) => u.tagKey === 'email'), [
    userInfoList,
  ]);

  const addressList1 = useMemo(
    () =>
      userInfoList.filter((u) => ['postNumber', 'address'].includes(u.tagKey)),
    [userInfoList]
  );

  const contactList1 = useMemo(
    () =>
      userInfoList.filter((u) =>
        ['phoneNumber', 'faxNumber'].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const addressList2 = useMemo(
    () =>
      userInfoList.filter((u) =>
        ['postNumberHome', 'addressHome'].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const contactList2 = useMemo(
    () =>
      userInfoList.filter((u) =>
        ['phoneNumberHome', 'faxNumberHome', 'workplace'].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const addressList3 = useMemo(
    () =>
      userInfoList.filter((u) =>
        ['postNumberWorkplace', 'addressWorkplace'].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const contactList3 = useMemo(
    () =>
      userInfoList.filter((u) =>
        ['phoneNumberWorkplace', 'faxNumberWorkplace'].includes(u.tagKey)
      ),
    [userInfoList]
  );

  const etcList = useMemo(
    () =>
      userInfoList.filter((u) => ['comment', 'birthday'].includes(u.tagKey)),
    [userInfoList]
  );

  const privilegePeriodList = useMemo(() => {
    return privilegePeriod
      .sort((a, b) => b.end.toMillis() - a.end.toMillis())
      .map((u) => [
        `${u.start.toDate().getFullYear()}年${
          u.start.toDate().getMonth() + 1
        }月${u.start.toDate().getDate()}日`,
        `${u.end.toDate().getFullYear()}年${
          u.end.toDate().getMonth() + 1
        }月${u.end.toDate().getDate()}日`,
      ]);
  }, [privilegePeriod]);

  const changeShowPrivilegePeriod = useCallback(() => {
    setShowPrivilegePeriod((showPrivilegePeriod) => !showPrivilegePeriod);
  }, []);

  const onContactUpdate = useCallback((contactType) => {
    const userID = firebase.auth().currentUser?.uid;
    const db = firebase.firestore();
    db.collection('Users')
      .where('userID', '==', userID)
      .get()
      .then((snapshot) => {
        if (snapshot.size === 0) return;
        const docID = snapshot.docs[0].id;
        if (contactType === 'email') {
          setEmailInvalid(false);
          db.collection('Users')
            .doc(docID)
            .update({
              emailValid: 'new',
            })
            .catch((error) => {
              console.error(error);
            });
        } else if (contactType === 'current') {
          setCurrentInvalid(false);
          db.collection('Users')
            .doc(docID)
            .update({
              currentValid: 'new',
            })
            .catch((error) => {
              console.error(error);
            });
        } else if (contactType === 'home') {
          setHomeInvalid(false);
          db.collection('Users')
            .doc(docID)
            .update({
              homeValid: 'new',
            })
            .catch((error) => {
              console.error(error);
            });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const onUserInfoChange = useCallback(
    (tagKey, value) => {
      if (tagKey === 'email') {
        onContactUpdate('email');
      }
      if (tagKey === 'address' || tagKey === 'postNumber') {
        onContactUpdate('current');
      }
      if (tagKey === 'addressHome' || tagKey === 'postNumberHome') {
        onContactUpdate('home');
      }
      setUserInfoList((userInfoList) => {
        const idx = userInfoList.findIndex((u) => u.tagKey === tagKey);
        const _userInfoList: UserInfoItem[] = userInfoList.slice();
        _userInfoList[idx].inputValue = value;
        return _userInfoList;
      });
    },
    [onContactUpdate]
  );

  return (
    <div>
      <div>{message}</div>
      <Root>
        {isLoading ? (
          <div>読み込み中...</div>
        ) : (
          <div>
            <br />
            <H3>
              会員特権 : {hasPrivilege(privilegePeriod) ? '有効' : '一時無効'}
            </H3>
            <Hr />
            <Explanation>
              会費を支払った年の年度末(2月末)まで特権が得られます。特権を持つ会員は、名簿閲覧の際に住所などの詳細情報を閲覧できる他、Web会誌(開発予定)閲覧、掲示板(開発予定)への投稿が可能になります。
              <br />
              <button onClick={changeShowPrivilegePeriod}>
                {showPrivilegePeriod
                  ? '特権期間履歴を非表示'
                  : '特権期間履歴を表示'}
              </button>
              <br />
              {showPrivilegePeriod
                ? privilegePeriodList.length === 0
                  ? 'データがありません'
                  : privilegePeriodList.map((u, i) => (
                      <div key={i}>
                        {u[0]}から{u[1]}まで
                      </div>
                    ))
                : undefined}
            </Explanation>
            <br />
            <H3>基本情報の編集</H3>
            <Hr />
            <Explanation>
              氏名などの基本情報を編集できます。テキストボックスの内容は編集後保存されます。「桃源会からの連絡先」に指定した連絡先には、桃源会からメールや郵便物が送られてくることがあります。連絡先の詳細を変更したい場合は、ページ下部の連絡先の欄から編集を行ってください。
            </Explanation>
            <MyPageContact
              userInfoList={userInfoList}
              emailInvalid={emailInvalid}
              currentInvalid={currentInvalid}
              homeInvalid={homeInvalid}
            />
            {basicInfoList.map(
              ({
                tagKey,
                tagName,
                privateIsDisabled,
                inputValue,
                isPublic,
                check,
              }) => (
                <MyPageInfo
                  key={tagKey}
                  tagKey={tagKey}
                  tagName={tagName}
                  privateIsDisabled={privateIsDisabled}
                  inputValue={inputValue}
                  isPublic={isPublic}
                  check={check}
                  onUserInfoChange={onUserInfoChange}
                />
              )
            )}
            <br />
            <H3>連絡先の編集</H3>
            <Hr />
            <Explanation>
              連絡先情報を編集できます。桃源会からの連絡手段として使用される他、名簿の追加情報欄に掲載されますので、引っ越しやメールアドレス変更、転職の際にご編集ください。情報を名簿に掲載したくない場合は、該当項目について「名簿に記載」ではなく「非公開」と指定してください。
            </Explanation>
            <MyPageInfo
              key={email?.tagKey ?? ''}
              tagKey={email?.tagKey ?? 'email'}
              tagName={email?.tagName ?? ''}
              privateIsDisabled={email?.privateIsDisabled ?? false}
              inputValue={email?.inputValue ?? ''}
              isPublic={email?.isPublic ?? false}
              check={email?.check ?? checkDummy}
              onUserInfoChange={onUserInfoChange}
            />
            <MyPageAddress
              addressList={addressList1}
              onUserInfoChange={onUserInfoChange}
            />
            {contactList1.map(
              ({
                tagKey,
                tagName,
                privateIsDisabled,
                inputValue,
                isPublic,
                check,
              }) => (
                <MyPageInfo
                  key={tagKey}
                  tagKey={tagKey}
                  tagName={tagName}
                  privateIsDisabled={privateIsDisabled}
                  inputValue={inputValue}
                  isPublic={isPublic}
                  check={check}
                  onUserInfoChange={onUserInfoChange}
                />
              )
            )}
            <MyPageAddress
              addressList={addressList2}
              onUserInfoChange={onUserInfoChange}
            />
            {contactList2.map(
              ({
                tagKey,
                tagName,
                privateIsDisabled,
                inputValue,
                isPublic,
                check,
              }) => (
                <MyPageInfo
                  key={tagKey}
                  tagKey={tagKey}
                  tagName={tagName}
                  privateIsDisabled={privateIsDisabled}
                  inputValue={inputValue}
                  isPublic={isPublic}
                  check={check}
                  onUserInfoChange={onUserInfoChange}
                />
              )
            )}
            <MyPageAddress
              addressList={addressList3}
              onUserInfoChange={onUserInfoChange}
            />
            {contactList3.map(
              ({
                tagKey,
                tagName,
                privateIsDisabled,
                inputValue,
                isPublic,
                check,
              }) => (
                <MyPageInfo
                  key={tagKey}
                  tagKey={tagKey}
                  tagName={tagName}
                  privateIsDisabled={privateIsDisabled}
                  inputValue={inputValue}
                  isPublic={isPublic}
                  check={check}
                  onUserInfoChange={onUserInfoChange}
                />
              )
            )}
            <br />
            <H3>その他情報の編集</H3>
            <Hr />
            <Explanation>
              誕生日は同姓同名の場合の個人識別などに使用することを想定しております。コメントは常識の範囲内でご自由にお書きください。コメントの隣の選択肢で「名簿に記載」を選択していただければ、名簿上にコメントが掲載され、誰でも閲覧することが可能です。
            </Explanation>
            {etcList.map(
              ({
                tagKey,
                tagName,
                privateIsDisabled,
                inputValue,
                isPublic,
                check,
              }) => (
                <MyPageInfo
                  key={tagKey}
                  tagKey={tagKey}
                  tagName={tagName}
                  privateIsDisabled={privateIsDisabled}
                  inputValue={inputValue}
                  isPublic={isPublic}
                  check={check}
                  onUserInfoChange={onUserInfoChange}
                />
              )
            )}
            <br />
            <Setting gotoPage={props.gotoPage} />
          </div>
        )}
      </Root>
    </div>
  );
});
